<script setup lang="ts">
  import { computed, ref, useSlots, watchEffect } from 'vue';

  import useMediaQuery from '../../composables/useMediaQuery/useMediaQuery';
  import Backdrop from '../Backdrop/Backdrop.vue';
  import Divider from '../Divider/Divider.vue';

  export interface AppSidebarProps {
    /**
     * Controls wether the sidebar is opened or not
     */
    isOpen?: boolean;
  }

  const props = withDefaults(defineProps<AppSidebarProps>(), {
    isOpen: false,
  });

  const slots = useSlots();

  const emit = defineEmits<{
    /**
     * @deprecated Use the `update:is-open` event instead
     */
    (e: 'dismiss'): void;
    (e: 'update:is-open', isOpen: boolean): void;
  }>();

  const isExtraLargeScreen = useMediaQuery('screen and (min-width: 1321px)');
  const isBackdropVisible = computed(() => props.isOpen && !isExtraLargeScreen.value);
  const initialPageScrollingElementStyle = ref({ height: '', overflow: '' });
  const isSidebarOpen = computed(() => props.isOpen || isExtraLargeScreen.value);

  function onBackdropClick() {
    emit('dismiss');
    emit('update:is-open', false);
  }

  function getPageScrollingElement(): HTMLElement | null {
    if (typeof document === 'undefined') return null;
    return (document.scrollingElement || document.body) as HTMLElement;
  }

  watchEffect(() => {
    if (isExtraLargeScreen.value) return;

    const el = getPageScrollingElement();
    if (!el) return;

    if (isBackdropVisible.value) {
      Object.assign(initialPageScrollingElementStyle.value, {
        overflow: el.style.overflow,
        height: el.style.height,
      });
    }

    Object.assign(el.style, {
      overflow: isBackdropVisible.value ? 'hidden' : initialPageScrollingElementStyle.value.overflow, // Prevents page from scrolling when AppSidebar is open
      height: isBackdropVisible.value ? '100%' : initialPageScrollingElementStyle.value.height, // Ensures the backdrop covers the entire page when AppSidebar is open
    });
  });
</script>

<template>
  <div
    id="stash-app-sidebar"
    class="stash-app-sidebar relative"
    :class="{
      'stash-app-sidebar--is-open': isSidebarOpen,
      'z-modal': isBackdropVisible,
      'z-[301]': !isBackdropVisible,
    }"
    data-test="stash-app-sidebar"
  >
    <Transition name="backdrop">
      <Backdrop v-show="isBackdropVisible" class="stash-app-sidebar__backdrop" @click.stop="onBackdropClick" />
    </Transition>
    <Transition name="aside">
      <aside
        v-show="isSidebarOpen"
        class="stash-app-sidebar__aside fixed flex h-screen flex-col bg-purple-500"
        :class="{ 'shadow-3xl': isBackdropVisible }"
        data-test="stash-app-sidebar__aside"
      >
        <Divider class="stash-app-sidebar__divider" />
        <div v-if="slots['app-context']" class="px-3 pt-6">
          <!-- @slot App Context component slot, ideally for rendering the context switcher -->
          <slot name="app-context"></slot>
        </div>
        <div v-if="slots['company-context']" class="px-3 pt-6">
          <!-- @slot Company Context component slot, ideally for rendering the company picker -->
          <slot name="company-context"></slot>
        </div>
        <nav v-if="slots['navigation']" class="pt-6">
          <ul role="menubar" aria-orientation="vertical">
            <!-- @slot Navigation component slot, for rendering navigation items -->
            <slot name="navigation"></slot>
          </ul>
        </nav>
        <div class="flex-1"></div>
        <!-- @slot Footer component slot, for rendering version/environment notes -->
        <slot name="footer"></slot>
      </aside>
    </Transition>
  </div>
</template>

<style module>
  @layer utilities {
    :global(:where(.stash-app-sidebar) .stash-app-sidebar__aside) {
      padding-top: calc(var(--height-topbar) - 1px); /* -1px to compensate for the border height */
      width: var(--width-sidebar);
    }

    :global(:where(.stash-app-sidebar) .stash-app-sidebar__divider::before),
    :global(:where(.stash-app-sidebar) .stash-app-sidebar__divider::after) {
      border-bottom: 1px solid rgb(255 255 255 / 12%);
    }

    :global(:where(.stash-app-sidebar) .backdrop-enter-active),
    :global(:where(.stash-app-sidebar) .backdrop-leave-active) {
      transition: opacity 200ms ease-in-out;
    }

    :global(:where(.stash-app-sidebar) .backdrop-enter-from),
    :global(:where(.stash-app-sidebar) .backdrop-leave-to) {
      opacity: 0;
    }

    :global(:where(.stash-app-sidebar) .aside-enter-active),
    :global(:where(.stash-app-sidebar) .aside-leave-active) {
      transition:
        opacity 200ms ease-in-out,
        transform 200ms ease-in-out;
    }

    :global(:where(.stash-app-sidebar) .aside-enter-from),
    :global(:where(.stash-app-sidebar) .aside-leave-to) {
      opacity: 0;
      transform: translateX(-20%);
    }
  }
</style>
